import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Science as StarIcon,
  Visibility as ViewIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const StarsList = ({ stars, planets = [], onStarSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStarId, setSelectedStarId] = useState(null);

  const filteredStars = useMemo(() => {
    if (!searchTerm) return stars;
    
    return stars.filter(star => 
      (star.name && star.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (star.spectral_class && star.spectral_class.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (star.star_system_id && star.star_system_id.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [stars, searchTerm]);

  const getPlanetsCount = (star) => {
    return planets.filter(planet => 
      planet.star_system_id === star.star_system_id || 
      planet.star_id === star.star_id
    ).length;
  };

  const handleStarClick = (star) => {
    setSelectedStarId(star.star_id);
    onStarSelect(star);
  };

  if (!stars || stars.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography color="text.secondary" textAlign="center">
            No stars data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StarIcon color="primary" />
          <Typography variant="h6">
            Stars ({stars.length})
          </Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search stars by name, spectral class, or system..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ mb: 2 }}
        />

        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredStars.map((star, index) => {
            const planetsCount = getPlanetsCount(star);
            
            return (
              <React.Fragment key={star.star_id || index}>
                <ListItem
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      onClick={() => handleStarClick(star)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                  }
                  disablePadding
                >
                  <ListItemButton onClick={() => handleStarClick(star)}>
                    <ListItemIcon>
                      <StarIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1">
                            {star.name || `Star ${star.star_id}`}
                          </Typography>
                          {star.spectral_class && (
                            <Chip 
                              label={star.spectral_class} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            System: {star.star_system_id || 'Unknown'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`${planetsCount} planets`} 
                              size="small" 
                              variant="outlined"
                            />
                            {star.is_main_sequence && (
                              <Chip 
                                label="Main Sequence" 
                                size="small" 
                                color="success"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < filteredStars.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>

        {filteredStars.length === 0 && (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            No stars found matching your search
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StarsList;